<template>
    <transition appear name="modal">
        <div class="ds-modal ds-modal--backdrop">
            <div
                role="dialog"
                :aria-labelledby="ariaLabelledBy"
                aria-modal="true"
                class="ds-container ds-height-full ds-display-flex ds-align-items-center ds-justify-content-center"
                data-j-modal>
                <div
                    v-click-outside="closeOnClickOutside ? close : null"
                    class="ds-display-flex ds-justify-content-center ds-width-full"
                    :class="{ 'ds-position-relative': closeButtonPosition === 'inside' }">
                    <button
                        v-if="withCloseButton"
                        type="button"
                        tabindex="0"
                        :aria-label="ariaLabelClose"
                        :class="[ closeButtonPosition === 'outside' ? 'ds-button-link--white' : 'ds-button-link--black']"
                        class="ds-modal__icon ds-button-link ds-button-link--primary ds-display-flex ds-align-items-center"
                        data-j-button
                        @click="close">
                        <DsIcon category="interface" name="cross" />
                    </button>
                    <slot></slot>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
/**
 * Modal component
 *
 * @example
 *
 * <DsModal
 *  :aria-labelled-by="your-aria-label"
 *  :aria-label-close="your-aria-label-close"
 *  :close-button-position="closeButtonPosition">
 *      your content here
 *  </DsModal>
 */
import { DsIcon } from '../../atoms';
import { clickOutside } from '../../utils/directives';

const INSIDE = 'inside';
const OUTSIDE = 'outside';

export default {
    name: 'DsModal',
    components: { DsIcon },
    directives: { clickOutside },
    props: {
        /**
         * Aria-labelledy label for the dialog
         */
        ariaLabelledBy: { type: String, required: true },
        /**
         * Aria label for the 'close' button
         */
        ariaLabelClose: { type: String, required: true },
        /**
         * Shows/hides the 'x' close icon. Displays it by default.
         */
        withCloseButton: { type: Boolean, default: true },
        /**
         * Closes the modal if the user clicks outside of the content box. Enabled by default. Set this prop to false if you want to force the user to click on the 'x' icon to close the modal.
         */
        closeOnClickOutside: { type: Boolean, default: true },
        /**
         * Position of close button
         * Possible values: 'inside', 'outside'. Defaults to 'outside'
         */
        closeButtonPosition: { type: String, default: OUTSIDE, validator: value => [INSIDE, OUTSIDE].includes(value) }
    },
    created () {
        document.addEventListener('keydown', this.closeOnEscKey);
    },
    beforeDestroy () {
        document.removeEventListener('keydown', this.closeOnEscKey);
    },
    methods: {
        close () {
            this.$emit('close-modal');
        },
        closeOnEscKey (e) {
            if (e.keyCode === 27) {
                this.$emit('close-modal');
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../../foundation/tokens/variables';
@import '../../foundation/mixins/mixins';
@import '../../foundation/mixins/utilities/multimedia.mixins';

.ds-modal {
    &--backdrop {
        background-color: rgba($color-black, 0.9);
        bottom: 0;
        left: 0;
        position: fixed;
        right: 0;
        top: 0;
    }

    &__icon {
        height: $spacing-24;
        margin: $spacing-24;
        position: absolute;
        right: 0;
        top: 0;
        width: $spacing-24;

        svg {
            height: 100%;
            width: 100%;
        }
    }
}

.modal-enter-active,
.modal-leave-active {
    transition: all 0.4s ease;
}

.modal-enter,
.modal-leave-to {
    opacity: 0;
}
</style>
